import React from 'react';
import { Link } from 'react-router-dom';

interface ServiceCardProps {
  id?: string;
  title?: string;
  description?: string;
  url?: string;
  icon?: React.ReactNode | string;
  badge?: string;
  className?: string;
  service?: {
    id: string;
    title: string;
    description: string;
    url: string;
    iconName: string;
    badge?: string;
  };
}

/**
 * Service card — neumorphic theme
 */
const ServiceCard: React.FC<ServiceCardProps> = ({
  id,
  title,
  description,
  url,
  icon,
  badge,
  className = '',
  service,
}) => {
  const serviceTitle = service?.title || title;
  const serviceDescription = service?.description || description;
  const serviceUrl = service?.url || url;
  const serviceBadge = service?.badge || badge;
  const serviceIcon = service?.iconName || icon;

  const defaultIcon = (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );

  return (
    <Link to={serviceUrl || '#'} className="block h-full">
      <div
        className={`flex flex-col h-full rounded-neu bg-neu-bg p-6 transition-all duration-300 ease-out hover:-translate-y-1 ${className}`}
        style={{
          boxShadow: '9px 9px 16px rgb(163,177,198,0.6), -9px -9px 16px rgba(255,255,255,0.5)',
        }}
      >
        {/* Icon + badge row */}
        <div className="flex justify-between items-start mb-5">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center text-neu-accent"
            style={{
              background: '#E0E5EC',
              boxShadow: 'inset 6px 6px 10px rgb(163,177,198,0.6), inset -6px -6px 10px rgba(255,255,255,0.5)',
            }}
          >
            {typeof serviceIcon === 'string' ? defaultIcon : (serviceIcon || defaultIcon)}
          </div>

          {serviceBadge && (
            <span
              className="text-xs font-display font-semibold px-3 py-1 rounded-full text-neu-accent"
              style={{
                background: '#E0E5EC',
                boxShadow: '3px 3px 6px rgb(163,177,198,0.6), -3px -3px 6px rgba(255,255,255,0.5)',
              }}
            >
              {serviceBadge}
            </span>
          )}
        </div>

        <h3 className="font-display text-xl font-semibold mb-2 text-neu-text">{serviceTitle}</h3>
        <p className="text-neu-muted text-sm mb-4 leading-relaxed flex-1">{serviceDescription}</p>

        <div className="mt-auto pt-2 flex items-center gap-1 text-neu-accent font-display font-medium text-sm">
          Learn more
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </Link>
  );
};

export default ServiceCard;
